import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import useCategoryMappingAuth from "./useCategoryMappingAuth";
import useDefaultConflictRules from "./useDefaultConflictRules";
import { PROTECTED_MAPPING_USERNAME, conflictDefaultsLabel, defaultConflictsHref, missingDefaultSites } from "./categoryDefaultConflicts";
import { isCancelledRead } from "./defaultConflictsApi";
import { BlockSkeleton } from "../interfaccia/loading";
import apiUrl from "../Apiurl";
import stilePulsanti from "../interfaccia/stilePulsanti";
import ModaArticoliAdvancedFiltersModal from "./ModaArticoliAdvancedFiltersModal";
import UserAvatarBadge from "./UserAvatarBadge";
import WebCategoryRequirementCreateModal from "./WebCategoryRequirementCreateModal";

const CATEGORY_MAPPING_MODA_FIELDS = [
  { key: "reparto", label: "Reparto", lookupUrl: "/moda/lookup/reparti" },
  { key: "categoria", label: "Categoria", lookupUrl: "/moda/lookup/categorie" },
  { key: "tipo", label: "Tipo", lookupUrl: "/moda/lookup/tipi" },
  { key: "sottotipo", label: "Sottotipo", lookupUrl: "/moda/lookup/sottotipi" },
  { key: "persona", label: "Persona", lookupUrl: "/moda/lookup/persona" },
];

const CATEGORY_MAPPING_MONGO_FIELDS = [
  { key: "stagioneConsigliata", label: "Stagione consigliata", path: "datiUlteriori.generici.stagioneConsigliata.valueId" },
  { key: "utilizzo", label: "Utilizzo (generico)", path: "datiUlteriori.generici.utilizzo.valueId" },
  { key: "calzatureUtilizzo", label: "Utilizzo calzature", path: "datiUlteriori.calzature.utilizzo.valueId" },
  { key: "sciUtilizzo", label: "Utilizzo sci", path: "datiUlteriori.sci.utilizzo.valueId" },
  { key: "skiBootsUtilizzo", label: "Utilizzo scarponi sci", path: "datiUlteriori.skiBoots.utilizzo.valueId" },
  { key: "bastoniSport", label: "Sport bastoni", path: "datiUlteriori.bastoni.sport.valueId" },
  { key: "bastoniUtilizzo", label: "Specialita sci bastoni", path: "datiUlteriori.bastoni.utilizzo.valueId" },
  { key: "bikeUtilizzo", label: "Utilizzo bike", path: "datiUlteriori.bike.utilizzo.valueId" },
  { key: "calzatureAspetto", label: "Aspetto calzature", path: "datiUlteriori.calzature.aspetto.valueId" },
  { key: "piscinaMareTipo", label: "Accessorio mare", path: "datiUlteriori.piscinaMare.tipo.valueId" },
  { key: "trekkingCampingAccessori", label: "Accessorio camping", path: "datiUlteriori.trekkingCamping.accessori.valueId" },
  { key: "arrampicataTipoAttrezzatura", label: "Attrezzatura arrampicata", path: "datiUlteriori.arrampicata.tipoAttrezzatura.valueId" },
  { key: "golfTipoAccessorioGolf", label: "Accessorio golf", path: "datiUlteriori.golf.tipoAccessorioGolf.valueId" },
];

const SITE_KEYS = ["VIG", "BOT"];

const getFirstFilledValue = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null) continue;
    const normalized = String(value).trim();
    if (normalized.length) return normalized;
  }
  return "";
};

const isValidAuditUsername = (value) =>
  Boolean(value) && !/^[A-Za-z0-9._-]+\|/.test(value);

const normalizeLookupRecords = (payload) => {
  const list = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload)
      ? payload
      : [];
  return list
    .map((item) => {
      if (item == null) return null;
      if (typeof item === "string") {
        const value = item.trim();
        return value ? { code: value, description: value } : null;
      }
      const code =
        item.code ??
        item.codice ??
        item.cod ??
        item.id ??
        item.value ??
        item.key ??
        item.ID;
      if (code === undefined || code === null || String(code).trim() === "") {
        return null;
      }
      const description =
        item.description ??
        item.descrizione ??
        item.label ??
        item.nomeDisplay ??
        item.nome_display ??
        item.nome ??
        item.name ??
        item.desc ??
        code;
      return {
        code: String(code).trim(),
        description: String(description ?? code).trim(),
      };
    })
    .filter(Boolean);
};

const firstPositiveFilterValue = (filters, key) => {
  const eqValue = getFirstFilledValue(filters?.[`${key}_eq`]);
  if (eqValue) return eqValue;
  const inValues = Array.isArray(filters?.[`${key}_in`])
    ? filters[`${key}_in`].filter((value) => getFirstFilledValue(value))
    : [];
  return inValues.length ? inValues[0] : "";
};

const buildCategoryMappingConditions = (filters) => {
  const conditions = [];
  CATEGORY_MAPPING_MODA_FIELDS.forEach((field) => {
    const key = field.key;
    const eqValue = getFirstFilledValue(filters?.[`${key}_eq`]);
    const inValues = Array.isArray(filters?.[`${key}_in`])
      ? filters[`${key}_in`].filter((value) => getFirstFilledValue(value))
      : [];
    const notInValues = Array.isArray(filters?.[`${key}_not_in`])
      ? filters[`${key}_not_in`].filter((value) => getFirstFilledValue(value))
      : [];
    if (eqValue) {
      conditions.push({ source: "moda", field: key, operator: "eq", value: eqValue });
    } else if (notInValues.length) {
      conditions.push({ source: "moda", field: key, operator: "not_in", values: notInValues });
    } else if (inValues.length) {
      conditions.push({ source: "moda", field: key, operator: "in", values: inValues });
    }
  });
  CATEGORY_MAPPING_MONGO_FIELDS.forEach((field) => {
    const key = field.key;
    const inValues = Array.isArray(filters?.mongoFilters?.[`${key}_in`])
      ? filters.mongoFilters[`${key}_in`].filter((value) => getFirstFilledValue(value))
      : [];
    const notInValues = Array.isArray(filters?.mongoFilters?.[`${key}_not_in`])
      ? filters.mongoFilters[`${key}_not_in`].filter((value) => getFirstFilledValue(value))
      : [];
    if (notInValues.length) {
      conditions.push({ source: "mongo", path: field.path, operator: "not_in", values: notInValues });
    } else if (inValues.length) {
      conditions.push({ source: "mongo", path: field.path, operator: "in", values: inValues });
    }
  });
  return conditions;
};

const buildCategoryMappingResolveInput = (filters) => {
  const moda = {};
  const mongo = {};
  CATEGORY_MAPPING_MODA_FIELDS.forEach((field) => {
    const value = firstPositiveFilterValue(filters, field.key);
    if (value) moda[field.key] = value;
  });
  CATEGORY_MAPPING_MONGO_FIELDS.forEach((field) => {
    const value = firstPositiveFilterValue(filters?.mongoFilters || {}, field.key);
    if (value) {
      const pathParts = field.path.split(".");
      let cursor = mongo;
      pathParts.forEach((part, index) => {
        if (index === pathParts.length - 1) {
          cursor[part] = value;
          return;
        }
        cursor[part] = cursor[part] || {};
        cursor = cursor[part];
      });
    }
  });
  return { sites: SITE_KEYS, moda, mongo };
};

const outputLabel = (output) =>
  output.sitePath || output.siteLabel || output.externalCategoryId || output.external_category_id || "";

const parseConditions = (rule) => {
  if (Array.isArray(rule?.conditions)) return rule.conditions;
  try {
    const parsed = JSON.parse(rule?.conditionsJson || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

const fieldLabel = (condition) => {
  const field = condition.field || condition.path || "campo";
  if (condition.source === "moda") {
    return CATEGORY_MAPPING_MODA_FIELDS.find((item) => item.key === field)?.label || field;
  }
  return CATEGORY_MAPPING_MONGO_FIELDS.find((item) => item.path === field)?.label || field;
};

const formatCondition = (condition) => {
  const operator = String(condition.operator || "eq").toUpperCase();
  const values = Array.isArray(condition.values)
    ? condition.values.join(", ")
    : condition.value ?? "";
  return `${fieldLabel(condition)} ${operator} ${values}`;
};

const filtersFromConditions = (conditions = []) => {
  const filters = { mongoFilters: {} };
  conditions.forEach((condition) => {
    const operator = condition.operator || "eq";
    if (condition.source === "moda") {
      const field = condition.field || condition.path;
      if (!CATEGORY_MAPPING_MODA_FIELDS.some((item) => item.key === field)) return;
      if (operator === "eq") filters[`${field}_eq`] = getFirstFilledValue(condition.value);
      if (operator === "in") filters[`${field}_in`] = Array.isArray(condition.values) ? condition.values.map(String) : [];
      if (operator === "not_in") filters[`${field}_not_in`] = Array.isArray(condition.values) ? condition.values.map(String) : [];
      return;
    }
    const path = condition.path || condition.field;
    const mongoField = CATEGORY_MAPPING_MONGO_FIELDS.find((item) => item.path === path);
    if (!mongoField) return;
    if (operator === "eq") filters.mongoFilters[`${mongoField.key}_in`] = [getFirstFilledValue(condition.value)].filter(Boolean);
    if (operator === "in") filters.mongoFilters[`${mongoField.key}_in`] = Array.isArray(condition.values) ? condition.values.map(String) : [];
    if (operator === "not_in") filters.mongoFilters[`${mongoField.key}_not_in`] = Array.isArray(condition.values) ? condition.values.map(String) : [];
  });
  return filters;
};

const outputsBySite = (rule) => {
  const grouped = { VIG: [], BOT: [] };
  (Array.isArray(rule?.outputs) ? rule.outputs : []).forEach((output) => {
    const site = String(output.site || "").toUpperCase();
    if (!grouped[site]) return;
    grouped[site].push(output);
  });
  SITE_KEYS.forEach((site) => {
    grouped[site].sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0));
  });
  return grouped;
};

const initialOutputsFromRule = (rule) => {
  const grouped = outputsBySite(rule);
  return {
    VIG: grouped.VIG
      .filter((output) => output.siteCategoryActive !== false)
      .map((output) => String(output.externalCategoryId)),
    BOT: grouped.BOT
      .filter((output) => output.siteCategoryActive !== false)
      .map((output) => String(output.externalCategoryId)),
  };
};

const initialDefaultOutputsFromRule = (rule) => {
  const grouped = outputsBySite(rule);
  const defaultForSite = (site) => {
    const defaultOutput = grouped[site].find(
      (output) => output.siteCategoryActive !== false && (output.isDefaultCandidate || rule.isDefaultCandidate),
    );
    return defaultOutput ? String(defaultOutput.externalCategoryId) : "";
  };
  return { VIG: defaultForSite("VIG"), BOT: defaultForSite("BOT") };
};

const ruleName = (rule) =>
  rule?.description || rule?.ruleKey || (rule?.id ? `Mappatura #${rule.id}` : "Nuova mappatura");

const normalizeSearchText = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase();

const conditionHasModaValue = (conditions, fieldKey, selectedValue) => {
  const normalizedValue = String(selectedValue);
  return conditions.some((condition) => {
    if (condition.source !== "moda") return false;
    if ((condition.field || condition.path) !== fieldKey) return false;
    const operator = String(condition.operator || "eq").toLowerCase();
    if (operator === "eq") return String(condition.value ?? "") === normalizedValue;
    if (operator === "in") {
      return Array.isArray(condition.values)
        ? condition.values.some((value) => String(value) === normalizedValue)
        : false;
    }
    return false;
  });
};

const emptyTableFilters = () => ({
  name: "",
  reparto: "",
  categoria: "",
  tipo: "",
  sottotipo: "",
  persona: "",
  requirements: "",
  outputs: "",
  cancelledOutputs: false,
});

const CategoryOutputPills = ({ outputs }) => {
  if (!outputs.length) return <span className="gray">-</span>;
  return (
    <div className="flex flex-wrap">
      {outputs.map((output) => {
        const cancelled = output.siteCategoryActive === false;
        return (
          <span
            key={`${output.site}-${output.externalCategoryId}-${output.id || output.priority}`}
            className={
              "dib br-pill ph2 pv1 mr2 mb2 f7 " +
              (cancelled ? "bg-washed-red dark-red ba b--light-red" : "")
            }
            style={
              cancelled
                ? undefined
                : {
                    backgroundColor: output.isDefaultCandidate
                      ? "var(--colTastoVerde, #137752)"
                      : "rgba(19, 119, 82, 0.08)",
                    color: output.isDefaultCandidate ? "#fff" : "inherit",
                  }
            }
            title={cancelled ? "Categoria annullata sul sito Prestashop" : undefined}
          >
            {outputLabel(output)}
            {output.isDefaultCandidate ? " - default" : ""}
            {cancelled ? " - annullata" : ""}
          </span>
        );
      })}
    </div>
  );
};

const WebCategoryAttributeRulesPage = ({ token, utente, temaColori, editorOnly = false, editRuleId, onRuleSaved, onEditorClose }) => {
  const { buttonClassesGreen, buttonClassesWhite } = stilePulsanti;
  const canManage = Boolean(utente?.permessiAPI?.["update:prestashop"]);
  const currentUsername = getFirstFilledValue(utente?.username);
  const hasValidCurrentUsername = isValidAuditUsername(currentUsername);
  const canCreate = canManage && hasValidCurrentUsername;
  const canEditMappingRule = (rule) => {
    if (!hasValidCurrentUsername) return false;
    const protectedUsername = normalizeSearchText(PROTECTED_MAPPING_USERNAME);
    const isProtected = [rule?.createdBy, rule?.updatedBy].some(
      (value) => normalizeSearchText(value) === protectedUsername,
    );
    return !isProtected || normalizeSearchText(currentUsername) === protectedUsername;
  };

  const [rules, setRules] = useState([]);
  const [siteCategories, setSiteCategories] = useState({ VIG: [], BOT: [] });
  const [lookupOptions, setLookupOptions] = useState({});
  const [datiUlterioriStandard, setDatiUlterioriStandard] = useState({});
  const [showInactive, setShowInactive] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [requirementsModalOpen, setRequirementsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [tableFilters, setTableFilters] = useState(() => emptyTableFilters());
  const [tablePage, setTablePage] = useState(1);
  const [tablePerPage, setTablePerPage] = useState(25);

  const getAuth = useCategoryMappingAuth();
  const dataRead = useRef(null);
  const pageMounted = useRef(true);
  const [conflictRevision, setConflictRevision] = useState(0);
  const conflictRules = useDefaultConflictRules(getAuth, !editorOnly, conflictRevision);

  const sortedRules = useMemo(
    () =>
      [...rules]
        .filter((rule) => showInactive || rule.active)
        .sort((a, b) => {
          if (Number(a.active) !== Number(b.active)) return Number(b.active) - Number(a.active);
          return Number(a.priority || 0) - Number(b.priority || 0) || Number(a.id) - Number(b.id);
        }),
    [rules, showInactive],
  );

  const filteredRules = useMemo(() => {
    const nameNeedle = normalizeSearchText(tableFilters.name);
    const requirementsNeedle = normalizeSearchText(tableFilters.requirements);
    const outputsNeedle = normalizeSearchText(tableFilters.outputs);
    const modaFilterFields = ["reparto", "categoria", "tipo", "sottotipo", "persona"];

    return sortedRules.filter((rule) => {
      const conditions = parseConditions(rule);
      const outputs = Array.isArray(rule.outputs) ? rule.outputs : [];
      if (nameNeedle && !normalizeSearchText(ruleName(rule)).includes(nameNeedle)) {
        return false;
      }
      if (
        modaFilterFields.some(
          (fieldKey) =>
            tableFilters[fieldKey] &&
            !conditionHasModaValue(conditions, fieldKey, tableFilters[fieldKey]),
        )
      ) {
        return false;
      }
      if (
        requirementsNeedle &&
        !normalizeSearchText(conditions.map(formatCondition).join(" ")).includes(requirementsNeedle)
      ) {
        return false;
      }
      if (
        tableFilters.cancelledOutputs &&
        !outputs.some((output) => output.siteCategoryActive === false)
      ) {
        return false;
      }
      if (outputsNeedle) {
        const outputText = outputs
          .map((output) =>
            [
              output.site,
              output.externalCategoryId,
              output.external_category_id,
              outputLabel(output),
            ].join(" "),
          )
          .join(" ");
        if (!normalizeSearchText(outputText).includes(outputsNeedle)) return false;
      }
      return true;
    });
  }, [sortedRules, tableFilters]);

  const filtersActive = Object.entries(tableFilters).some(([key, value]) =>
    key === "cancelledOutputs" ? Boolean(value) : Boolean(getFirstFilledValue(value)),
  );
  const tableTotalPages = Math.max(1, Math.ceil(filteredRules.length / tablePerPage));
  const safeTablePage = Math.min(tablePage, tableTotalPages);
  const pagedRules = useMemo(
    () => filteredRules.slice((safeTablePage - 1) * tablePerPage, safeTablePage * tablePerPage),
    [filteredRules, safeTablePage, tablePerPage],
  );

  const activeRulesCount = rules.filter((rule) => rule.active).length;
  const inactiveRulesCount = rules.length - activeRulesCount;
  const editing = Boolean(editingRule?.id);

  const updateTableFilter = (key, value) => {
    setTablePage(1);
    setTableFilters((current) => ({ ...current, [key]: value }));
  };

  const resetTableFilters = () => {
    setTablePage(1);
    setTableFilters(emptyTableFilters());
  };

  const updateShowInactive = (checked) => {
    setTablePage(1);
    setShowInactive(checked);
  };

  const updateTablePerPage = (value) => {
    setTablePage(1);
    setTablePerPage(Number(value) || 25);
  };

  const modalInitialFilters = useMemo(
    () => (editingRule ? filtersFromConditions(parseConditions(editingRule)) : {}),
    [editingRule],
  );
  const modalInitialOutputs = useMemo(
    () => (editingRule ? initialOutputsFromRule(editingRule) : { VIG: [], BOT: [] }),
    [editingRule],
  );
  const modalInitialDefaultOutputs = useMemo(
    () => (editingRule ? initialDefaultOutputsFromRule(editingRule) : { VIG: "", BOT: "" }),
    [editingRule],
  );

  const loadData = useCallback(async () => {
    dataRead.current?.abort();
    const controller = new AbortController();
    dataRead.current = controller;
    const { signal } = controller;
    setLoading(true);
    setError("");
    try {
      const authHeaders = { ...await getAuth(), signal };
      if (signal.aborted) return;
      const [rulesResponse, vigResponse, botResponse, datiResponse, ...lookupResponses] = await Promise.all([
        axios.get(apiUrl + "/prodotti/categories/attribute-rules", authHeaders),
        axios.get(apiUrl + "/prodotti/categories/site-categories?site=VIG", authHeaders),
        axios.get(apiUrl + "/prodotti/categories/site-categories?site=BOT", authHeaders),
        axios.get(apiUrl + "/prodotti/get/datiUlteriori", authHeaders),
        ...CATEGORY_MAPPING_MODA_FIELDS.map((field) => axios.get(apiUrl + field.lookupUrl, authHeaders)),
      ]);
      if (signal.aborted) return;
      setRules(Array.isArray(rulesResponse?.data?.data) ? rulesResponse.data.data : []);
      setSiteCategories({
        VIG: Array.isArray(vigResponse?.data?.data) ? vigResponse.data.data : [],
        BOT: Array.isArray(botResponse?.data?.data) ? botResponse.data.data : [],
      });
      setDatiUlterioriStandard(datiResponse?.data || {});
      setLookupOptions(
        Object.fromEntries(
          CATEGORY_MAPPING_MODA_FIELDS.map((field, index) => [
            field.key,
            normalizeLookupRecords(lookupResponses[index]?.data),
          ]),
        ),
      );
    } catch (loadError) {
      if (isCancelledRead(loadError, signal)) return;
      setError(loadError?.response?.data?.error || loadError.message || "Errore caricamento map categorie");
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [getAuth]);

  useEffect(() => {
    pageMounted.current = true;
    loadData();
    return () => { pageMounted.current = false; dataRead.current?.abort(); };
  }, [loadData]);

  useEffect(() => {
    if (!editorOnly || !editRuleId || loading) return;
    const rule = rules.find((item) => Number(item.id) === Number(editRuleId));
    if (!rule) return;
    const protectedRule = [rule.createdBy, rule.updatedBy].some((value) => normalizeSearchText(value) === PROTECTED_MAPPING_USERNAME);
    if (!canManage || !hasValidCurrentUsername || (protectedRule && normalizeSearchText(currentUsername) !== PROTECTED_MAPPING_USERNAME)) {
      setError("Mappatura protetta o permessi insufficienti: modifica non disponibile per questo utente.");
      return;
    }
    setEditingRule(rule);
    setModalOpen(true);
  }, [editorOnly, editRuleId, loading, rules, canManage, hasValidCurrentUsername, currentUsername]);

  const resolveExistingMappings = useCallback(
    async (filters, options = {}) => {
      const response = await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules/resolve",
        buildCategoryMappingResolveInput(filters),
        { ...await getAuth(), signal: options.signal },
      );
      return response?.data?.data || {};
    },
    [getAuth],
  );

  const openNewMapping = () => {
    setEditingRule(null);
    setError("");
    setMessage("");
    setModalOpen(true);
  };

  const openNewRequirement = () => {
    setError("");
    setMessage("");
    setRequirementsModalOpen(true);
  };

  const openEditMapping = (rule) => {
    if (!canManage || !canEditMappingRule(rule)) return;
    setEditingRule(rule);
    setError("");
    setMessage("");
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setEditingRule(null);
    onEditorClose?.();
  };

  const saveMappingFromModal = async (payload) => {
    if (!canManage) return;
    if (!hasValidCurrentUsername) {
      setError("Username utente mancante o non valido: aggiorna la sessione prima di salvare.");
      return;
    }
    if (editingRule && !canEditMappingRule(editingRule)) {
      setError("Questa mappatura puo essere modificata solo da d.fenoglio@vigliettisport.com.");
      return;
    }
    const filters = payload?.filters || {};
    const mappingName = getFirstFilledValue(payload?.categoryMappingName);
    const categoryOutputs = payload?.categoryOutputs || {};
    const categoryDefaultOutputs = payload?.categoryDefaultOutputs || {};
    const conditions = buildCategoryMappingConditions(filters);
    const missingSites = missingDefaultSites(categoryOutputs, categoryDefaultOutputs);
    if (missingSites.length) {
      setError(`Scegliere la categoria predefinita per il sito ${missingSites.join(", ")}.`);
      return;
    }
    const outputs = SITE_KEYS.flatMap((site) => {
      const siteOutputs = Array.isArray(categoryOutputs?.[site])
        ? categoryOutputs[site]
        : [];
      const defaultOutput = categoryDefaultOutputs?.[site] || "";
      return siteOutputs.map((externalCategoryId, index) => ({
        site,
        externalCategoryId,
        isDefaultCandidate: String(externalCategoryId) === String(defaultOutput),
        priority: index,
      }));
    });

    if (!conditions.length) {
      setError("Seleziona almeno un requisito MODA o Mongo per creare la mappatura.");
      return;
    }
    if (!outputs.length) {
      setError("Seleziona almeno una categoria VIG o BOT come output.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    try {
      await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules",
        {
          id: editingRule?.id || undefined,
          username: currentUsername,
          ruleKey: editingRule?.ruleKey || "",
          description: mappingName || editingRule?.description || "Mappatura categorie creata da pagina Map categorie",
          priority: editingRule ? Number(editingRule.priority || 0) : 0,
          active: editingRule ? Boolean(editingRule.active) : true,
          isDefaultCandidate: false,
          conditionsJson: JSON.stringify(conditions, null, 2),
          outputs,
        },
        await getAuth(),
      );
      setMessage(editingRule ? "Mappatura aggiornata." : "Mappatura creata.");
      setModalOpen(false);
      setEditingRule(null);
      setConflictRevision((value) => value + 1);
      if (onRuleSaved) await onRuleSaved();
      else await loadData();
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError.message || "Errore salvataggio mappatura");
    } finally {
      if (pageMounted.current) setSaving(false);
    }
  };

  const saveNewRequirement = async (payload) => {
    if (!canManage) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const resolutionResponse = await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules/resolve",
        { sites: payload.sites, moda: payload.moda, mongo: {} },
        await getAuth(),
      );
      const resolution = resolutionResponse?.data?.data || {};
      const existingSites = payload.sites.filter(
        (site) =>
          resolution?.[site]?.mappingRequirement?.directSources?.length > 0,
      );
      if (
        existingSites.length > 0 &&
        !window.confirm(
          "Esiste gia una non sufficienza diretta per " +
            existingSites.join(", ") +
            " con gli stessi filtri. Sostituirne i dati integrativi?",
        )
      ) {
        return;
      }

      await axios.post(
        apiUrl + "/prodotti/categories/attribute-rules/moda-combinations/requirements",
        { ...payload, username: currentUsername },
        await getAuth(),
      );
      setRequirementsModalOpen(false);
      setMessage(
        existingSites.length > 0
          ? "Non sufficienza aggiornata."
          : "Non sufficienza creata.",
      );
    } finally {
      if (pageMounted.current) setSaving(false);
    }
  };

  const deleteRule = async (rule) => {
    if (!canManage || !rule?.id || !canEditMappingRule(rule)) return;
    if (!window.confirm("Eliminare la mappatura selezionata?")) return;
    setSaving(true);
    setError("");
    setMessage("");
    try {
      await axios.delete(
        apiUrl + "/prodotti/categories/attribute-rules/" + rule.id,
        { ...await getAuth(), data: { username: currentUsername } },
      );
      setMessage("Mappatura eliminata.");
      setModalOpen(false);
      setEditingRule(null);
      setConflictRevision((value) => value + 1);
      if (onRuleSaved) await onRuleSaved();
      else await loadData();
    } catch (deleteError) {
      setError(deleteError?.response?.data?.error || deleteError.message || "Errore eliminazione mappatura");
    } finally {
      if (pageMounted.current) setSaving(false);
    }
  };

  const mappingModal = (
      <ModaArticoliAdvancedFiltersModal
        open={modalOpen}
        onClose={closeModal}
        onApply={saveMappingFromModal}
        initialFilters={modalInitialFilters}
        lookupOptions={lookupOptions}
        datiUlterioriStandard={datiUlterioriStandard}
        temaColori={temaColori || "rootTema"}
        categoryMappingMode
        categoryMappingSaveError={error}
        editingCategoryMappingRuleId={editingRule?.id}
        categoryMappingTitle={editing ? `Modifica ${ruleName(editingRule)}` : "Nuova mappatura categorie"}
        initialCategoryMappingName={editing ? ruleName(editingRule) : ""}
        initialCategoryMappingPanel={editing ? "mapping" : "existing"}
        siteCategories={siteCategories}
        initialCategoryMappingOutputs={modalInitialOutputs}
        initialCategoryMappingDefaultOutputs={modalInitialDefaultOutputs}
        onResolveExistingMappings={resolveExistingMappings}
        onDeleteCategoryMapping={editing ? () => deleteRule(editingRule) : undefined}
      />
  );

  if (editorOnly) return <>
    {error && <div className="pa3 scrittaRosso" role="alert">{error}</div>}
    {loading && <BlockSkeleton height="10rem" />}
    {!loading && !rules.some((rule) => Number(rule.id) === Number(editRuleId)) &&
      <div role="alert">Mappatura non disponibile.</div>}
    {mappingModal}
  </>;

  return (
    <div className={"pa3 " + (temaColori || "")}>
      <div className="sfondoBiancoTema pa3 br3">
        <div className="flex flex-wrap items-start justify-between mb3">
          <div>
            <h2 className="ma0">Map categorie</h2>
            <div className="f6 mt1">Tabella delle mappature categorie web e dei relativi output sito.</div>
            <div className="f7 gray mt2">
              Attive: {activeRulesCount} - Disattive: {inactiveRulesCount}
            </div>
          </div>
          <div className="mt2 mt0-l">
            <button type="button" className={buttonClassesWhite} onClick={() => { loadData(); setConflictRevision((value) => value + 1); }} disabled={loading}>
              Aggiorna
            </button>
            <button
              type="button"
              className={buttonClassesWhite + " ml2"}
              onClick={openNewRequirement}
              disabled={!canCreate || saving}
            >
              Nuova non sufficienza
            </button>
            <button
              type="button"
              className={buttonClassesGreen + " ml2"}
              onClick={openNewMapping}
              disabled={!canCreate || saving}
            >
              Nuova mappatura
            </button>
          </div>
        </div>

        {error && <div className="pa3 mb3 bg-washed-red dark-red">{error}</div>}
        {message && <div className="pa3 mb3 bg-washed-green dark-green">{message}</div>}
        {!canManage && <div className="pa3 mb3 bg-washed-yellow">Modalita lettura: per modificare serve update:prestashop.</div>}
        {canManage && !hasValidCurrentUsername && (
          <div className="pa3 mb3 bg-washed-red dark-red">
            Username utente mancante o non valido: aggiorna la sessione prima di creare o modificare mappature.
          </div>
        )}

        {conflictRules.loading && <div className="f7 mb2">Verifica segnalazioni di default doppio in corso (massimo 1000 articoli per combinazione).</div>}
        {conflictRules.error && <div className="scrittaRosso mb2" role="alert">Segnalazioni conflitti non disponibili: {conflictRules.error}</div>}
        <label className="dib mb3 pointer f6">
          <input type="checkbox" checked={showInactive} onChange={(event) => updateShowInactive(event.target.checked)} /> Mostra anche disattive
        </label>

        <div className="mb3 pa2 ba b--black-10 br2">
          <div className="flex flex-wrap items-end nl1 nr1">
            <div className="w-100 w-20-m w-15-l ph1 mb2">
              <label className="db f7 ttu tracked gray mb1" htmlFor="map-category-filter-name">Nome</label>
              <input
                id="map-category-filter-name"
                type="text"
                className="input-reset ba b--silver br2 pa2 w-100"
                value={tableFilters.name}
                onChange={(event) => updateTableFilter("name", event.target.value)}
                placeholder="Cerca nome"
              />
            </div>
            {CATEGORY_MAPPING_MODA_FIELDS.map((field) => (
              <div key={field.key} className="w-50 w-20-m w-10-l ph1 mb2">
                <label className="db f7 ttu tracked gray mb1" htmlFor={`map-category-filter-${field.key}`}>
                  {field.label}
                </label>
                <select
                  id={`map-category-filter-${field.key}`}
                  className="input-reset ba b--silver br2 pa2 w-100 bg-white"
                  value={tableFilters[field.key]}
                  onChange={(event) => updateTableFilter(field.key, event.target.value)}
                >
                  <option value="">Tutti</option>
                  {(lookupOptions[field.key] || []).map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.description || option.code}
                    </option>
                  ))}
                </select>
              </div>
            ))}
            <div className="w-50-m w-15-l ph1 mb2">
              <label className="db f7 ttu tracked gray mb1" htmlFor="map-category-filter-requirements">Requisiti</label>
              <input
                id="map-category-filter-requirements"
                type="text"
                className="input-reset ba b--silver br2 pa2 w-100"
                value={tableFilters.requirements}
                onChange={(event) => updateTableFilter("requirements", event.target.value)}
                placeholder="Cerca nei requisiti"
              />
            </div>
            <div className="w-50-m w-15-l ph1 mb2">
              <label className="db f7 ttu tracked gray mb1" htmlFor="map-category-filter-outputs">Output VIG/BOT</label>
              <input
                id="map-category-filter-outputs"
                type="text"
                className="input-reset ba b--silver br2 pa2 w-100"
                value={tableFilters.outputs}
                onChange={(event) => updateTableFilter("outputs", event.target.value)}
                placeholder="Cerca categoria output"
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between mt1">
            <div className="flex flex-wrap items-center f7 gray mb2 mb0-l">
              <span className="mr3">
                Mostrate {filteredRules.length} mappature su {sortedRules.length}
              </span>
              <label className="pointer dark-red">
                <input
                  type="checkbox"
                  className="mr1"
                  checked={tableFilters.cancelledOutputs}
                  onChange={(event) =>
                    updateTableFilter("cancelledOutputs", event.target.checked)
                  }
                />
                Categorie annullate
              </label>
            </div>
            <div className="flex items-center">
              <label className="f7 gray mr2" htmlFor="map-category-page-size">Righe</label>
              <select
                id="map-category-page-size"
                className="ba b--silver br2 pa1 bg-white mr2"
                value={tablePerPage}
                onChange={(event) => updateTablePerPage(event.target.value)}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              {filtersActive && (
                <button type="button" className={buttonClassesWhite} onClick={resetTableFilters}>
                  Pulisci filtri
                </button>
              )}
            </div>
          </div>
        </div>

        {loading ? (
          <BlockSkeleton height="20rem" />
        ) : sortedRules.length === 0 ? (
          <div className="pa3 ba b--black-10">Nessuna mappatura da mostrare.</div>
        ) : filteredRules.length === 0 ? (
          <div className="pa3 ba b--black-10">Nessuna mappatura corrisponde ai filtri.</div>
        ) : (
          <div className="overflow-auto">
            <table className="collapse w-100 f6">
              <thead>
                <tr className="tl bb b--black-10">
                  <th className="pa2">Nome</th>
                  <th className="pa2 tc">Creato</th>
                  <th className="pa2 tc">Modificato</th>
                  <th className="pa2 tr">Priorita</th>
                  <th className="pa2">Requisiti</th>
                  <th className="pa2">Output BOT</th>
                  <th className="pa2">Output VIG</th>
                </tr>
              </thead>
              <tbody>
                {pagedRules.map((rule) => {
                  const conditions = parseConditions(rule);
                  const groupedOutputs = outputsBySite(rule);
                  const canEditRule = canManage && canEditMappingRule(rule);
                  return (
                    <tr key={rule.id} className="bb b--black-05 v-top">
                      <td className="pa2" style={{ minWidth: "14rem" }}>
                        <button
                          type="button"
                          className="button-reset bg-transparent bn pa0 tl pointer fw6 underline-hover"
                          onClick={() => openEditMapping(rule)}
                          disabled={!canEditRule}
                          title={
                            canEditRule
                              ? "Apri mappatura"
                              : canManage
                                ? "Mappatura protetta: modificabile solo da d.fenoglio@vigliettisport.com"
                                : "Permesso update:prestashop necessario"
                          }
                        >
                          {ruleName(rule)}
                        </button>
                        {Object.values(conflictRules.byRule[rule.id] || {}).map(({ key, site, conflict }) => (
                          <a key={key + site} className="db scrittaRosso f7 fw6 mt1"
                            href={defaultConflictsHref(key)} aria-label={`${site}: Default doppio`} title={conflictDefaultsLabel(conflict)}>
                            {site}: Default doppio
                          </a>
                        ))}
                        <div className="f7 gray mt1">
                          #{rule.id} - {rule.active ? "attiva" : "disattiva"}
                        </div>
                      </td>
                      <td className="pa2 tc">
                        <UserAvatarBadge username={rule.createdBy} />
                      </td>
                      <td className="pa2 tc">
                        <UserAvatarBadge username={rule.updatedBy} />
                      </td>
                      <td className="pa2 tr fw6">{Number(rule.priority || 0)}</td>
                      <td className="pa2" style={{ minWidth: "18rem" }}>
                        {conditions.length === 0 ? (
                          <span className="gray">Wildcard completa</span>
                        ) : (
                          <div className="flex flex-wrap">
                            {conditions.map((condition, index) => (
                              <span key={index} className="dib br-pill ph2 pv1 mr2 mb2 f7 bg-near-white">
                                {formatCondition(condition)}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="pa2" style={{ minWidth: "14rem" }}>
                        <CategoryOutputPills outputs={groupedOutputs.BOT} />
                      </td>
                      <td className="pa2" style={{ minWidth: "14rem" }}>
                        <CategoryOutputPills outputs={groupedOutputs.VIG} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredRules.length > 0 && (
          <div className="flex flex-wrap items-center justify-between mt3">
            <button
              type="button"
              className={buttonClassesWhite}
              disabled={safeTablePage <= 1}
              onClick={() => setTablePage(Math.max(1, safeTablePage - 1))}
            >
              Precedente
            </button>
            <div className="f7 gray tc mv2 mv0-l">
              Pagina {safeTablePage} di {tableTotalPages} · righe {(safeTablePage - 1) * tablePerPage + 1}-{Math.min(safeTablePage * tablePerPage, filteredRules.length)} di {filteredRules.length}
            </div>
            <button
              type="button"
              className={buttonClassesWhite}
              disabled={safeTablePage >= tableTotalPages}
              onClick={() => setTablePage(Math.min(tableTotalPages, safeTablePage + 1))}
            >
              Successiva
            </button>
          </div>
        )}
      </div>

      <WebCategoryRequirementCreateModal
        open={requirementsModalOpen}
        onClose={() => setRequirementsModalOpen(false)}
        onSave={saveNewRequirement}
        modaFields={CATEGORY_MAPPING_MODA_FIELDS}
        mongoFields={CATEGORY_MAPPING_MONGO_FIELDS}
        lookupOptions={lookupOptions}
        buttonClassesGreen={buttonClassesGreen}
        buttonClassesWhite={buttonClassesWhite}
        saving={saving}
      />

      {mappingModal}
    </div>
  );
};

export default WebCategoryAttributeRulesPage;
